import { mergeTargetingAnswers, normalizeTargetingAnswers } from '@/helpers/streamsTargeting';

const QUESTIONNAIRE_STYLE_ID = 'storeeel-questionnaire-style';
const QUESTIONNAIRE_ROOT_ID = 'storeeel-questionnaire-root';

const escapeHtml = (value) => String(value)
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#39;');

const normalizeId = (value) => value == null ? '' : String(value);

const sortByPosition = (left, right) => {
  const leftPosition = Number(left?.position ?? Number.MAX_SAFE_INTEGER);
  const rightPosition = Number(right?.position ?? Number.MAX_SAFE_INTEGER);
  if (leftPosition !== rightPosition) return leftPosition - rightPosition;
  return normalizeId(left?.id).localeCompare(normalizeId(right?.id), 'ja');
};

const sortQuestionnaireGroups = (questionnaireGroups) => [...questionnaireGroups].sort((left, right) => {
  // APIの返却順に依存せず、questionnaire.position の昇順で表示順を固定する。
  const positionOrder = sortByPosition(left?.questionnaire, right?.questionnaire);
  if (positionOrder !== 0) return positionOrder;
  return normalizeId(left?.segmentKeyId || left?.questionnaire?.id)
    .localeCompare(normalizeId(right?.segmentKeyId || right?.questionnaire?.id), 'ja');
});

const isSegmentQuestionnaire = (group) => group?.questionnaire?.kind === 'segment';

const ensureQuestionnaireStyles = () => {
  if (document.getElementById(QUESTIONNAIRE_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = QUESTIONNAIRE_STYLE_ID;
  style.textContent = `
    .storeeel-questionnaire-overlay {
      position: fixed;
      inset: 0;
      z-index: 10000000000;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 16px;
      animation: storeeel-questionnaire-fade-in 220ms ease;
    }

    .storeeel-questionnaire-overlay.storeeel-questionnaire-overlay-static {
      animation: none;
    }

    .storeeel-questionnaire-overlay * {
      box-sizing: border-box;
    }

    .storeeel-questionnaire-backdrop {
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.6);
      backdrop-filter: blur(10px);
    }

    .storeeel-questionnaire-panel {
      position: relative;
      width: min(375px, calc(100vw - 32px));
      height: min(700px, calc(100vh - 32px));
      max-height: 700px;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 24px;
      background:
        radial-gradient(circle at top, rgba(31, 111, 235, 0.18), transparent 42%),
        linear-gradient(180deg, #232323 0%, #1a1a1a 100%);
      box-shadow: 0 32px 80px rgba(0, 0, 0, 0.45);
      color: #ffffff;
      font-family: "Hiragino Sans", "Yu Gothic", sans-serif;
      animation: storeeel-questionnaire-slide-up 240ms ease;
    }

    .storeeel-questionnaire-overlay.storeeel-questionnaire-overlay-static .storeeel-questionnaire-panel {
      animation: none;
    }

    .storeeel-questionnaire-close {
      position: absolute;
      top: 16px;
      right: 16px;
      width: 32px;
      height: 32px;
      border: none;
      border-radius: 9999px;
      background: transparent;
      color: #a3a3a3;
      cursor: pointer;
      transition: background-color 160ms ease, color 160ms ease;
    }

    .storeeel-questionnaire-close:hover {
      background: rgba(255, 255, 255, 0.08);
      color: #ffffff;
    }

    .storeeel-questionnaire-header {
      padding: 32px 24px 16px;
    }

    .storeeel-questionnaire-step {
      margin: 0 0 8px;
      color: #a3a3a3;
      font-size: 12px;
      letter-spacing: 0.22em;
      text-transform: uppercase;
    }

    .storeeel-questionnaire-title {
      margin: 0;
      font-size: 28px;
      font-weight: 700;
      line-height: 1.35;
    }

    .storeeel-questionnaire-description {
      margin: 8px 0 0;
      color: #a3a3a3;
      font-size: 14px;
      line-height: 1.6;
    }

    .storeeel-questionnaire-body {
      flex: 1;
      overflow-y: auto;
      padding: 16px 24px;
      scrollbar-width: thin;
      scrollbar-color: #525252 transparent;
    }

    .storeeel-questionnaire-body::-webkit-scrollbar {
      width: 5px;
    }

    .storeeel-questionnaire-body::-webkit-scrollbar-thumb {
      border-radius: 9999px;
      background: #525252;
    }

    .storeeel-questionnaire-section + .storeeel-questionnaire-section {
      margin-top: 20px;
    }

    .storeeel-questionnaire-group-button,
    .storeeel-questionnaire-option,
    .storeeel-questionnaire-chip {
      border: 1px solid #595959;
      color: #ffffff;
      cursor: pointer;
      transition: border-color 160ms ease, background-color 160ms ease, box-shadow 160ms ease;
    }

    .storeeel-questionnaire-group-button {
      margin-bottom: 10px;
      padding: 6px 12px;
      border-radius: 12px;
      background: #393939;
      color: #a3a3a3;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.06em;
    }

    .storeeel-questionnaire-chip-list {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
    }

    .storeeel-questionnaire-chip {
      padding: 10px 16px;
      border-radius: 9999px;
      background: #2d2d2d;
      font-size: 14px;
      font-weight: 500;
    }

    .storeeel-questionnaire-option-list {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .storeeel-questionnaire-option {
      width: 100%;
      padding: 14px 16px;
      border-radius: 14px;
      background: #2d2d2d;
      font-size: 14px;
      font-weight: 500;
      text-align: left;
    }

    .storeeel-questionnaire-group-button.is-selected,
    .storeeel-questionnaire-chip.is-selected,
    .storeeel-questionnaire-option.is-selected {
      border-color: #3b97ff;
      background: #0c64d8;
      box-shadow: 0 12px 28px rgba(12, 100, 216, 0.28);
      color: #ffffff;
    }

    .storeeel-questionnaire-summary {
      padding: 0 24px 8px;
      color: #3b97ff;
      font-size: 12px;
      font-weight: 600;
    }

    .storeeel-questionnaire-summary:empty {
      display: none;
    }

    .storeeel-questionnaire-footer {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      padding: 16px 24px 32px;
    }

    .storeeel-questionnaire-next {
      width: 100%;
      padding: 14px 16px;
      border: none;
      border-radius: 14px;
      background: linear-gradient(180deg, #2391ff 0%, #0c64d8 100%);
      color: #ffffff;
      font-size: 14px;
      font-weight: 700;
      cursor: pointer;
      box-shadow: 0 18px 36px rgba(12, 100, 216, 0.25);
      transition: filter 160ms ease;
    }

    .storeeel-questionnaire-next:hover {
      filter: brightness(1.08);
    }

    .storeeel-questionnaire-next:disabled {
      background: linear-gradient(180deg, #6b7280 0%, #525252 100%);
      box-shadow: none;
      filter: none;
      cursor: not-allowed;
    }

    .storeeel-questionnaire-skip {
      border: none;
      background: transparent;
      color: #a3a3a3;
      font-size: 12px;
      text-decoration: underline;
      cursor: pointer;
    }

    @keyframes storeeel-questionnaire-fade-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes storeeel-questionnaire-slide-up {
      from {
        opacity: 0;
        transform: translateY(16px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `;

  document.head.appendChild(style);
};

const getQuestionnaireDescription = (group) => {
  if (isSegmentQuestionnaire(group)) return 'ひとつ選択してください（任意）';
  return '選択してください';
};

const getSelectedSegmentValueId = (group, answers) => normalizeId(answers?.segments?.[group?.segmentKeyId]);

const renderSegmentOptions = (group, answers) => {
  const selectedValue = getSelectedSegmentValueId(group, answers);
  return `
    <div class="storeeel-questionnaire-option-list">
      ${(Array.isArray(group?.questionnaire?.options) ? group.questionnaire.options : []).map((option) => {
        const optionId = normalizeId(option?.segmentValueId);
        return `
          <button
            type="button"
            class="storeeel-questionnaire-option${selectedValue === optionId ? ' is-selected' : ''}"
            data-storeeel-questionnaire-option
            data-storeeel-questionnaire-action="select-segment"
            data-storeeel-segment-key-id="${escapeHtml(group?.segmentKeyId || '')}"
            data-storeeel-segment-value-id="${escapeHtml(optionId)}"
          >
            ${escapeHtml(option?.label || '')}
          </button>
        `;
      }).join('')}
    </div>
  `;
};

const renderQuestionnaireBody = (group, answers) => renderSegmentOptions(group, answers);

const renderQuestionnaireSummary = (group, answers) => {
  const selectedValue = getSelectedSegmentValueId(group, answers);
  if (!selectedValue) return '';

  const option = (Array.isArray(group?.questionnaire?.options) ? group.questionnaire.options : [])
    .find((item) => normalizeId(item?.segmentValueId) === selectedValue);
  return option?.label ? `選択中: ${escapeHtml(option.label)}` : '';
};

// セグメント設問は任意回答にし、未選択でも次の設問や完了へ進める。
const canProceed = () => true;

const renderQuestionnaireMarkup = (questionnaireGroups, answers, stepIndex) => {
  const currentGroup = questionnaireGroups[stepIndex];
  const nextLabel = stepIndex >= questionnaireGroups.length - 1 ? '完了' : '次へ';

  return `
    <div class="storeeel-questionnaire-backdrop" data-storeeel-questionnaire-action="dismiss"></div>
    <div
      class="storeeel-questionnaire-panel"
      role="dialog"
      aria-modal="true"
      aria-label="ターゲティングアンケート"
    >
      <button
        type="button"
        class="storeeel-questionnaire-close"
        aria-label="閉じる"
        data-storeeel-questionnaire-action="dismiss"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>
      <div class="storeeel-questionnaire-header">
        <p class="storeeel-questionnaire-step">ステップ ${stepIndex + 1} / ${questionnaireGroups.length}</p>
        <h2 class="storeeel-questionnaire-title">${escapeHtml(currentGroup?.questionnaire?.name || '')}</h2>
        <p class="storeeel-questionnaire-description">${escapeHtml(getQuestionnaireDescription(currentGroup))}</p>
      </div>
      <div class="storeeel-questionnaire-body">
        ${renderQuestionnaireBody(currentGroup, answers)}
      </div>
      <div class="storeeel-questionnaire-summary">${renderQuestionnaireSummary(currentGroup, answers)}</div>
      <div class="storeeel-questionnaire-footer">
        <button
          type="button"
          class="storeeel-questionnaire-next"
          data-storeeel-questionnaire-next
          data-storeeel-questionnaire-action="next"
          ${canProceed(currentGroup, answers) ? '' : 'disabled'}
        >
          ${nextLabel}
        </button>
        <button
          type="button"
          class="storeeel-questionnaire-skip"
          data-storeeel-questionnaire-action="dismiss"
        >
          アンケートに回答しない
        </button>
      </div>
    </div>
  `;
};

export const showQuestionnaireModal = ({
  questionnaireGroups = [],
  initialAnswers = {},
  onComplete,
  onCancel,
} = {}) => {
  if (document.getElementById(QUESTIONNAIRE_ROOT_ID)) return;
  if (!Array.isArray(questionnaireGroups) || questionnaireGroups.length === 0) {
    if (typeof onComplete === 'function') onComplete(normalizeTargetingAnswers(initialAnswers));
    return;
  }

  const sortedQuestionnaireGroups = sortQuestionnaireGroups(questionnaireGroups);

  ensureQuestionnaireStyles();

  const previousOverflow = document.body.style.overflow;
  const root = document.createElement('div');
  root.id = QUESTIONNAIRE_ROOT_ID;
  root.className = 'storeeel-questionnaire-overlay';
  root.dataset.storeeelQuestionnaireModal = 'true';

  const state = {
    stepIndex: 0,
    answers: mergeTargetingAnswers(initialAnswers),
  };

  const cleanup = () => {
    root.remove();
    document.body.style.overflow = previousOverflow;
    document.removeEventListener('keydown', handleKeydown);
  };

  const finish = () => {
    cleanup();
    if (typeof onComplete === 'function') onComplete(mergeTargetingAnswers(state.answers));
  };

  const cancel = () => {
    cleanup();
    if (typeof onCancel === 'function') onCancel();
  };

  const render = ({ preserveScroll = false, animate = false } = {}) => {
    const previousBody = root.querySelector('.storeeel-questionnaire-body');
    const previousScrollTop = preserveScroll ? previousBody?.scrollTop ?? 0 : 0;

    // 選択のたびに再描画しても、初回表示時のフェード演出を再発火させない。
    root.classList.toggle('storeeel-questionnaire-overlay-static', !animate);
    root.innerHTML = renderQuestionnaireMarkup(sortedQuestionnaireGroups, state.answers, state.stepIndex);

    if (preserveScroll) {
      const body = root.querySelector('.storeeel-questionnaire-body');
      if (body) body.scrollTop = previousScrollTop;
    }
  };

  const handleAction = (action, target) => {
    switch (action) {
      case 'dismiss':
        cancel();
        return;
      case 'next':
        if (!canProceed()) return;
        if (state.stepIndex >= sortedQuestionnaireGroups.length - 1) {
          finish();
          return;
        }
        state.stepIndex += 1;
        render();
        return;
      case 'select-segment':
        // 同じ選択肢を再クリックしたときは任意回答として選択解除できるようにする。
        if (state.answers.segments[target.dataset.storeeelSegmentKeyId] === target.dataset.storeeelSegmentValueId) {
          delete state.answers.segments[target.dataset.storeeelSegmentKeyId];
        } else {
          state.answers.segments[target.dataset.storeeelSegmentKeyId] = target.dataset.storeeelSegmentValueId;
        }
        render();
        return;
      default:
        return;
    }
  };

  const handleClick = (event) => {
    const target = event.target.closest('[data-storeeel-questionnaire-action]');
    if (!target) return;
    handleAction(target.dataset.storeeelQuestionnaireAction, target);
  };

  const handleKeydown = (event) => {
    if (event.key === 'Escape') cancel();
  };

  root.addEventListener('click', handleClick);
  document.body.appendChild(root);
  document.body.style.overflow = 'hidden';
  document.addEventListener('keydown', handleKeydown);
  render({ animate: true });
};
